<template>
  <div class="tab-content">
    <div class="self-watch-box">
      <b-button
        type="is-primary"
        outlined
        class="self-watch-btn"
        :disabled="!currentUser"
        @click="$emit('toggle-current-user')"
      >
        {{
          isCurrentUserWatcher
            ? $t('notifications_modal.remove_me_from_watchers')
            : $t('notifications_modal.add_me_as_watcher')
        }}
      </b-button>
    </div>

    <div v-if="canManageWatchers" class="form-group">
      <label class="label">{{ $t('notifications_modal.add_watchers') }}</label>
      <b-field>
        <b-autocomplete
          :value="watcherSearch"
          :data="filteredUsers"
          :placeholder="$t('notifications_modal.search_users')"
          icon="magnify"
          :open-on-focus="true"
          @input="updateSearch"
          @select="onSelectWatcher"
        >
          <template slot-scope="props">
            <div class="user-option">
              <strong>{{ props.option.fullname }}</strong>
              <small>{{ props.option._id }}</small>
            </div>
          </template>
        </b-autocomplete>
      </b-field>
    </div>

    <div class="watchers-list">
      <label class="label">{{
        $t('notifications_modal.active_watchers')
      }}</label>
      <div v-if="watchers.length === 0" class="empty-state">
        {{ $t('notifications_modal.no_watchers') }}
      </div>
      <div v-else class="watcher-items">
        <div
          v-for="watcher in watchers"
          :key="watcher._id"
          class="watcher-item"
        >
          <span class="watcher-name">{{ watcher.fullname }}</span>
          <span class="watcher-email">{{ watcher._id }}</span>
          <b-button
            v-if="canManageWatchers"
            type="is-danger"
            size="is-small"
            icon-left="delete"
            @click="$emit('remove-watcher', watcher)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'watchers-tab',
  props: {
    watchers: {
      type: Array,
      required: true,
    },
    users: {
      type: Array,
      required: true,
    },
    watcherSearch: {
      type: String,
      default: '',
    },
    canManageWatchers: {
      type: Boolean,
      default: false,
    },
    currentUser: {
      type: Object,
      default: null,
    },
  },
  computed: {
    isCurrentUserWatcher() {
      if (!this.currentUser) {
        return false;
      }

      const currentUserKeys = this.getIdentityKeys(this.currentUser);
      if (!currentUserKeys.length) {
        return false;
      }

      return this.watchers.some((watcher) =>
        this.getIdentityKeys(watcher).some((key) =>
          currentUserKeys.includes(key)
        )
      );
    },
    filteredUsers() {
      if (!this.watcherSearch) {
        return this.users.filter(
          (u) => !this.watchers.find((w) => w._id === u._id)
        );
      }

      const search = this.watcherSearch.toLowerCase();
      return this.users
        .filter(
          (u) =>
            (u.fullname && u.fullname.toLowerCase().includes(search)) ||
            (u._id && u._id.toLowerCase().includes(search))
        )
        .filter((u) => !this.watchers.find((w) => w._id === u._id));
    },
  },
  methods: {
    getIdentityKeys(user) {
      if (!user) {
        return [];
      }

      return [user.email, user._id, user.id]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());
    },
    updateSearch(value) {
      this.$emit('update:watcherSearch', value);
    },
    onSelectWatcher(user) {
      this.$emit('add-watcher', user);
      this.$emit('update:watcherSearch', '');
    },
  },
};
</script>

<style scoped>
.tab-content {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.self-watch-box {
  display: flex;
  justify-content: center;
  padding: 0;
  margin-bottom: 20px;
  background: transparent;
  border: none;
}

.self-watch-btn {
  align-self: center;
}

.label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #363636;
}

.watchers-list {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e8e8e8;
}

.empty-state {
  text-align: center;
  color: #7a7a7a;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 4px;
}

.watcher-items {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.watcher-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f9f4fd;
  border: 1px solid #eadcf3;
  border-radius: 999px;
  width: fit-content;
}

.watcher-name {
  font-weight: 600;
}

.watcher-email {
  font-size: 0.875rem;
  color: #7a7a7a;
}

.user-option {
  padding: 8px;
}

.user-option strong {
  display: block;
}

.user-option small {
  display: block;
  color: #7a7a7a;
  font-size: 0.875rem;
}
</style>
